import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SettingsService } from '../../settings/settings.service';

export interface QuoteQuery {
  searchParam: string;
  userId: number;
  docStatus: string;
  fromDate: string | null;
  toDate: string | null;
  sortField: string;
  sortDirection: 'asc' | 'desc' | string;
  limit: number;
  offset: number;
  activeFlag: 0 | 1; // 1 = active, 0 = archived
  operationType?: string; // defaults to 'supplierQuote'
}

interface CacheEntry<T> {
  subject: BehaviorSubject<T | null>;
  updatedAt: number;
  inFlight?: Promise<void>;
}

@Injectable({ providedIn: 'root' })
export class SupplierQuoteSWRService {
  private cache = new Map<string, CacheEntry<any>>();
  private readonly TTL_MS = 90_000; // 90 seconds

  constructor(private settings: SettingsService) {}

  getList$(q: QuoteQuery, revalidate = true): Observable<any | null> {
    const key = this.keyOf(q);
    let entry = this.cache.get(key);
    if (!entry) {
      entry = { subject: new BehaviorSubject<any | null>(null), updatedAt: 0 };
      this.cache.set(key, entry);
    }

    const isFresh = Date.now() - entry.updatedAt < this.TTL_MS;
    if ((!isFresh || entry.subject.getValue() === null) && revalidate) {
      this.refresh(q);
    }
    return entry.subject.asObservable();
  }

  getCached(q: QuoteQuery): any | null {
    const key = this.keyOf(q);
    const entry = this.cache.get(key);
    return entry ? entry.subject.getValue() : null;
  }

  async refresh(q: QuoteQuery): Promise<void> {
    const key = this.keyOf(q);
    const entry = this.cache.get(key) || {
      subject: new BehaviorSubject<any | null>(null),
      updatedAt: 0,
    };
    this.cache.set(key, entry);
    if (entry.inFlight) return entry.inFlight;

    entry.inFlight = (async () => {
      const resp = await this.settings.fetchQuoteDetailsBySupplierId(
        q.searchParam,
        q.userId,
        q.docStatus,
        q.fromDate,
        q.toDate,
        q.sortField,
        q.sortDirection,
        q.limit,
        q.offset,
        q.activeFlag,
        q.operationType ?? 'supplierQuote'
      );
      entry.updatedAt = Date.now();
      entry.subject.next(resp);
      entry.inFlight = undefined;
    })().catch(() => {
      entry.inFlight = undefined;
    });

    return entry.inFlight;
  }

  invalidateMatching(predicate: (key: string) => boolean) {
    [...this.cache.keys()].forEach((k) => {
      if (predicate(k)) this.cache.delete(k);
    });
  }

  invalidateAll() {
    this.cache.clear();
  }

  keyOf(q: QuoteQuery): string {
    // Stable key for query
    return 'quote:' + JSON.stringify(q);
  }

  // Mutate a cached response in-place to avoid UI refetch where possible
  patchList(q: QuoteQuery, mutator: (resp: any) => any): void {
    const key = this.keyOf(q);
    const entry = this.cache.get(key);
    if (!entry) return;
    const current = entry.subject.getValue();
    if (!current) return;
    try {
      const next = mutator(current);
      entry.subject.next(next);
      entry.updatedAt = Date.now();
    } catch {
      // ignore mutator errors
    }
  }
}
